import { z } from 'zod';

/**
 * Supported currencies by LessonUp
 * Make sure to allow these in stripe as well
 * Follows the ISO 4217 standard
 */
export type SupportedCurrency = 'USD' | 'EUR' | 'GBP';

/**
 * note: this list is retrieved from Stripe's EU-country list for credit cards.
 * @link https://stripe.com/docs/currencies
 */
const COUNTRY_CODES_TO_CURRENCY: Record<string, SupportedCurrency | undefined> = {
  AD: 'EUR',
  AT: 'EUR',
  BE: 'EUR',
  BG: 'EUR',
  HR: 'EUR',
  CY: 'EUR',
  CZ: 'EUR',
  DK: 'EUR',
  EE: 'EUR',
  FO: 'GBP',
  FI: 'EUR',
  FR: 'EUR',
  DE: 'EUR',
  GI: 'GBP',
  GR: 'EUR',
  GL: 'EUR',
  GG: 'GBP',
  VA: 'EUR',
  HU: 'EUR',
  IS: 'EUR',
  IE: 'EUR',
  IM: 'GBP',
  IL: 'EUR',
  IT: 'EUR',
  JE: 'GBP',
  LV: 'EUR',
  LI: 'EUR',
  LT: 'EUR',
  LU: 'EUR',
  MT: 'EUR',
  MC: 'EUR',
  NL: 'EUR',
  NO: 'EUR',
  PL: 'EUR',
  PT: 'EUR',
  RO: 'EUR',
  PM: 'EUR',
  SM: 'EUR',
  SK: 'EUR',
  SI: 'EUR',
  ES: 'EUR',
  SE: 'EUR',
  TR: 'EUR',
  GB: 'GBP',
};

const FALLBACK_CURRENCY: SupportedCurrency = 'USD';

export const DbSupportedCurrencySchema = z.enum(['eur', 'gbp', 'usd']);

export type DbSupportedCurrency = z.infer<typeof DbSupportedCurrencySchema>;

export function supportedCurrencyForCountry(countryCode: string): SupportedCurrency {
  const currency = countryCode ? COUNTRY_CODES_TO_CURRENCY[countryCode.toUpperCase()] : undefined;
  return currency ?? FALLBACK_CURRENCY;
}

export function supportedCurrencyFromString(stringValue: string): SupportedCurrency | null {
  const currency = stringValue.toUpperCase();
  switch (currency) {
    case 'USD':
    case 'GBP':
    case 'EUR':
      return currency;
    default:
      return null;
  }
}

export function isValidCurrency(currency: string | undefined | null): currency is DbSupportedCurrency {
  const parsedCurrency = DbSupportedCurrencySchema.safeParse(currency);

  return parsedCurrency.success;
}
